import { useState } from "react";
import posthog from "posthog-js";

import { formatBlueprintForAPI } from "../../../../utils";

import useInvalidateUpdatedSnippets from "./useInvalidateUpdatedSnippets";

import { usePlayground } from "@/components/Playground/playground-store";
import { useAuth } from "@/context/auth-context";
import { useUser } from "@/context/user-context";
import { useNavigateWithSearch } from "@/hooks";
import { useCreatePromptTemplate, useCreatePromptVersion } from "@/queries";
import { PromptVersion } from "@/types/apiGetters";
import { parseErrorMessage } from "@/utils/errors";
import { displayErrorToast } from "@/utils/toast";

const useSaveTemplate = () => {
  const userToken = useAuth()?.userToken;
  const createPromptTemplateMutation = useCreatePromptTemplate(userToken || "");
  const updatePromptTemplateMutation = useCreatePromptVersion(userToken || "");
  const workspaceId = useUser()?.activeWorkspaceId;
  const navigateWithSearch = useNavigateWithSearch();
  const playground = usePlayground();
  const [isWaitingForSessionSave, setIsWaitingForSessionSave] = useState(false);

  const invalidateUpdatedSnippets = useInvalidateUpdatedSnippets();

  const handleCreate = async (name: string, shouldUseSnippetOverrides = false) => {
    setIsWaitingForSessionSave(true);
    await playground.waitForSessionSaveCompletion();

    createPromptTemplateMutation.mutate(
      {
        prompt_template: {
          prompt_name: name,
          workspace_id: workspaceId!,
          ...(playground.isSnippet ? { is_snippet: true } : {}),
        },
        prompt_version: formatBlueprintForAPI(
          {
            ...playground.blueprint,
          },
          playground.input_variable_sets,
        ),
        snippet_overrides: shouldUseSnippetOverrides
          ? playground.snippetOverrideStore.snippetOverrides
          : undefined,
      },
      {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onError: (error: any) => {
          displayErrorToast(
            error?.message || "An error occurred while creating the prompt template",
          );
        },
        onSuccess: data => {
          invalidateUpdatedSnippets();
          posthog.capture("Created Prompt Template");
          posthog.people.set_once({
            "First Prompt Created": true,
          });
          if ("success" in data) {
            const message =
              typeof data.message === "string" ? data.message : parseErrorMessage(data.message);
            displayErrorToast(message ?? "An error occurred while creating the prompt template");
            return;
          }
          playground.reassignInitialPayload();
          const { prompt_id } = data.prompt_version;
          setTimeout(() => navigateWithSearch(`/workspace/${workspaceId}/prompt/${prompt_id}`), 1);
        },
        onSettled: () => {
          setIsWaitingForSessionSave(false);
        },
      },
    );
  };

  const handleUpdate = (
    templateId: number,
    data: {
      commitMessage?: string;
      evaluationId?: number;
    },
    shouldUseSnippetOverrides = false,
  ): Promise<PromptVersion> =>
    new Promise((resolve, reject) => {
      setIsWaitingForSessionSave(true);
      void playground.waitForSessionSaveCompletion().then(() => {
        updatePromptTemplateMutation.mutate(
          {
            ...formatBlueprintForAPI(
              {
                ...playground.blueprint,
              },
              playground.input_variable_sets,
            ),
            commit_message: data.commitMessage,
            report_id: data.evaluationId,
            prompt_id: templateId,
            parent_version_id: playground.selectedTemplateVersion?.id,
            snippet_overrides: shouldUseSnippetOverrides
              ? playground.snippetOverrideStore.snippetOverrides
              : undefined,
          },
          {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            onError: (error: any) => {
              displayErrorToast(
                error?.message || "An error occurred while updating the prompt template",
              );
              reject();
            },
            onSuccess: responseData => {
              invalidateUpdatedSnippets();
              posthog.capture("Updated Prompt Template");
              if ("success" in responseData) {
                const message =
                  typeof responseData.message === "string"
                    ? responseData.message
                    : parseErrorMessage(responseData.message);
                displayErrorToast(
                  message ?? "An error occurred while updating the prompt template",
                );
                return;
              }
              playground.reassignInitialPayload();
              resolve(responseData);
            },
            onSettled: () => {
              setIsWaitingForSessionSave(false);
            },
          },
        );
      });
    });

  return {
    handleCreate,
    handleUpdate,
    isCreating: createPromptTemplateMutation.isLoading || isWaitingForSessionSave,
    isUpdating: updatePromptTemplateMutation.isLoading || isWaitingForSessionSave,
  };
};

export default useSaveTemplate;
